import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BugReport, Settings } from "@mui/icons-material";
import { Tooltip } from "@mui/material";

import { Character, CharacterSheetInterface } from '@dndModels';

import CheckBox from "@components/input/CheckBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import FlexBox from '@components/layout/FlexBox';
import DandDAPI from '@utilities/smart_api';
import CenteredMenu from '@components/input/menus/CenteredMenu';
import CharacterStatsCustomizations from './CharacterStatsCustomizations';
import CharacterCreatureTypeCustomization from './CharacterCreatureTypeCustomization';
import CharacterSizeCustomizations from './CharacterSizeCustomizations';
import CharacterPBCustomizations from './CharacterPBCustomizations';
import CharacterSpeedCustomizations from './CharacterSpeedCustomizations';
import CharacterInitCustomizations from './CharacterInitCustomizations';
import CharacterACCustomizations from './CharacterACCustomizations';
import CharacterMaxHPCustomizations from './CharacterMaxHPCustomizations';
import CharacterSavingThrowCustomizations from './CharacterSavingThrowCustomizations';
import InfoBox from '@components/input/InfoBox';
import CharacterSkillCustomizations from './CharacterSkillCustomizations';
import CharacterSenseCustomizations from './CharacterSenseCustomizations';
import CharacterDMCustomizations from './CharacterDMCustomizations';
import CharacterArmorTrainingCustomizations from './CharacterArmorTrainingCustomizations';
import CharacterWeaponProfCustomizations from './CharacterWeaponProfCustomizations';
import CharacterWeaponMasteryCustomizations from './CharacterWeaponMasteryCustomizations';
import CharacterToolProfCustomizations from './CharacterToolProfCustomizations';
import FloatMenu from '@components/input/menus/FloatMenu';
import CharacterResourceCustomizations from './CharacterResourceCustomizations';
import CharacterAttunementCustomizations from './CharacterAttunementCustomizations';
import CharacterSpellcastingCustomizations from './CharacterSpellcastingCustomizations';

interface Props {
  char: CharacterSheetInterface;
  onOpen?: () => void;
  onClose?: () => void;
}

const api = DandDAPI.getInstance();

export default function CharacterSettings({
  char,
  onOpen,
  onClose,
}: Props) {
  const [open, setOpen] = useState(false);
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [view, setView] = useState("Customizations");
  const [customization_type, setCustomizationType] = useState("Attributes");
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const recalculated = useCallback(() => {
    console.log("Char Race Button - Modal Closed");
    setReloading(true);
  }, []);

  useEffect(() => {
    char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    char.subscription_holder.subscribe("Resources", mounted, recalculated);
  }, [char, recalculated]);

  const renderView = () => {
    if (char instanceof Character) {
      switch (view) {
        case "Character Settings": return renderCharacterSettings();
        case "Customizations": return renderCustomizations();
      }
    } else {
      return renderCustomizations();
    }
    return null;
  }

  const renderCharacterSettings = () => {
    if (char instanceof Character) {
      return (
        <>
          { char.c_char &&
            <FlexBox direction="row">
              <FlexBox>
                <CheckBox
                  name="Hide Character from other Players in Campaign"
                  value={char.c_char.hide_char}
                  onChange={ async (changed) => {
                    if (char.c_char) {
                      char.c_char.hide_char = changed;
                      await api.upsertObject("campaign_char", char.c_char);
                      setPing(!ping);
                    }
                  }}
                />
              </FlexBox>
              <FlexBox>
                <CheckBox
                  name="Hide Details from other Players in Campaign"
                  value={char.c_char.hide_details}
                  onChange={ async (changed) => {
                    if (char.c_char) {
                      char.c_char.hide_details = changed;
                      await api.upsertObject("campaign_char", char.c_char);
                      setPing(!ping);
                    }
                  }}
                />
              </FlexBox>
            </FlexBox>
          }
          <FlexBox direction="row">
            <FlexBox>
              <CheckBox
                name="Show Hidden"
                value={char.show_hidden}
                onChange={(changed) => {
                  char.show_hidden = changed;
                  char.subscription_holder.displayChanged();
                  setPing(!ping);
                }}
              />
            </FlexBox>
            <FlexBox>
              <CheckBox
                name="Show Extra Action Types"
                value={char.show_extra_action_types}
                onChange={(changed) => {
                  char.show_extra_action_types = changed;
                  char.subscription_holder.displayChanged();
                  setPing(!ping);
                }}
              />
            </FlexBox>
            <FlexBox>
              <CheckBox
                name="Show Rituals"
                value={char.show_rituals}
                onChange={(changed) => {
                  char.show_rituals = changed;
                  char.subscription_holder.displayChanged();
                  setPing(!ping);
                }}
              />
            </FlexBox>
          </FlexBox>
        </>
      );
    }
    return null;
  }

  const renderCustomizations = () => {
    return (
      <>
        <FlexBox>
          <InfoBox label="Customizations" info_name="Character Customizations">
            <>
              <p>WARNING! This Feature is not yet fully implemented, so this info may not be completely accurate.</p>
              <p>
                Customizations either upgrade or override things in your character sheet. 
                This can be very powerful, and should never be done without your GM's permission, 
                which is why we have made it so they can only be used if the Character Options 
                on your Campaign allow you to do so. 
                The GM can choose to make it so only they can do customizations, 
                and then they may give you customizations that you can't give yourself.
              </p>
              <p>
                Many customizations are also available in controls for the things they're tied to.
                We don't have all customization controls here, but there are more here than anywhere else.
                The ones that are not found here are about specific things, 
                like customizing a specific ability or spell. 
                The customizations for those kinds of things would be in the controls for that particular thing.
                If there is something you want to customize, but don't see a way to do so, 
                it's probably a customization that we haven't gotten to yet. 
                You can chat with the System Admin using the chat bubble in the bottom right of the page to 
                request a type of customization we haven't gotten to yet. 
                We will get to these requests as quickly as we can, but we're a small team (currently a team of 1), 
                so please be patient.
              </p>
              <p>
                One type of customization in particular that will only be available here is Custom Abilities.
                These are complicated, as they allow you to define how the ability works, 
                using tools similar to those we use for defining how the various features granted by your 
                Species, Background, Class(es), Feats, etc. work. 
                It's inherently more complicated, but it can do all kinds of things, so it may be worth it.
              </p>
              <p>
                Also if customizations are done to a creature which is in an encounter, 
                grouped with others of the same stat block, the customizations will apply to all of them.
                If you don't want this to be the case, you can split the group before applying the customizations.
              </p>
            </>
          </InfoBox>
        </FlexBox>
        <FlexBox>
          <FloatMenu options={[
            "Attributes","Saving Throws","Skills","Speeds","Senses","Defenses",
            "Armor Training","Weapon Proficiencies","Weapon Masteries",
            "Tools","Resources","Spellcasting","Custom Abilities"
          ]} selected={customization_type} onSelect={setCustomizationType} />
        </FlexBox>
        { renderCustomizationType() }
      </>
    );
  }

  const renderCustomizationType = () => {
    switch (customization_type) {
      case "Attributes": return renderAttributes();
      case "Saving Throws": return renderSavingThrows();
      case "Skills": return renderSkills();
      case "Speeds": return renderSpeeds();
      case "Senses": return renderSenses();
      case "Defenses": return renderDMs();
      case "Armor Training": return renderArmorTraining();
      case "Weapon Proficiencies": return renderWeaponProfs();
      case "Weapon Masteries": return renderWeaponMasteries();
      case "Tools": return renderTools();
      case "Resources": return renderResources();
      case "Spellcasting": return renderSpellcasting();
      case "Custom Abilities": return renderCustomAbilities();
    }
    return null;
  }

  const renderArmorTraining = () => {
    return (
      <>
        <FlexBox className="header centered">
          Armor Training
        </FlexBox>
        <CharacterArmorTrainingCustomizations char={char} />
      </>
    );
  }

  const renderWeaponProfs = () => {
    return (
      <>
        <FlexBox className="header centered">
          Weapon Proficiencies
        </FlexBox>
        <CharacterWeaponProfCustomizations char={char} />
      </>
    );
  }

  const renderWeaponMasteries = () => {
    return (
      <>
        <FlexBox className="header centered">
          Weapon Masteries
        </FlexBox>
        <CharacterWeaponMasteryCustomizations char={char} />
      </>
    );
  }

  const renderTools = () => {
    return (
      <>
        <FlexBox className="header centered">
          Tool Proficiencies
        </FlexBox>
        <CharacterToolProfCustomizations char={char} />
      </>
    );
  }

  const renderResources = () => {
    return (
      <>
        <FlexBox className="header centered">
          Resources
        </FlexBox>
        <CharacterResourceCustomizations char={char} />
      </>
    );
  }

  const renderSpellcasting = () => {
    if (char instanceof Character) {
      return (
        <>
          <FlexBox className="header centered">
            Spellcasting
          </FlexBox>
          <CharacterSpellcastingCustomizations char={char} />
        </>
      );
    } else {
      return (<FlexBox className="header centered">Spellcasting Customization is only supported for Characters</FlexBox>);
    }
  }

  const renderAttributes = () => {
    return (
      <>
        { renderStatCustomizations() }
        <CharacterMaxHPCustomizations char={char} />
        <CharacterCreatureTypeCustomization char={char} />
        <CharacterSizeCustomizations char={char} />
        <CharacterPBCustomizations char={char} />
        <CharacterInitCustomizations char={char} />
        <CharacterACCustomizations char={char} />
        <CharacterAttunementCustomizations char={char} />
      </>
    );
  }

  const renderStatCustomizations = () => {
    return (
      <>
        <FlexBox className="header centered">
          Ability Scores
        </FlexBox>
        <FlexBox>
          <CharacterStatsCustomizations char={char} />
        </FlexBox>
        <FlexBox><hr /></FlexBox>
      </>
    );
  }

  const renderSavingThrows = () => {
    return (
      <>
        <FlexBox className="header centered">
          Saving Throws
        </FlexBox>
        <FlexBox>
          <CharacterSavingThrowCustomizations char={char} />
        </FlexBox>
        <FlexBox><hr /></FlexBox>
      </>
    );
  }

  const renderSkills = () => {
    return (
      <>
        <FlexBox className="header centered">
          Skills
        </FlexBox>
        <FlexBox>
          <CharacterSkillCustomizations char={char} />
        </FlexBox>
        <FlexBox><hr /></FlexBox>
      </>
    );
  }

  const renderSpeeds = () => {
    return (
      <>
        <FlexBox className="header centered">
          Speeds
        </FlexBox>
        <FlexBox>
          <CharacterSpeedCustomizations char={char} />
        </FlexBox>
        <FlexBox><hr /></FlexBox>
      </>
    );
  }

  const renderDMs = () => {
    return (
      <>
        <FlexBox className="header centered">
          Defenses
        </FlexBox>
        <FlexBox>
          <CharacterDMCustomizations char={char} />
        </FlexBox>
        <FlexBox><hr /></FlexBox>
      </>
    );
  }

  const renderSenses = () => {
    return (
      <>
        <FlexBox className="header centered">
          Senses
        </FlexBox>
        <FlexBox>
          <CharacterSenseCustomizations char={char} />
        </FlexBox>
        <FlexBox><hr /></FlexBox>
      </>
    );
  }

  const renderCustomAbilities = () => {
    return (
      <>
      </>
    );
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={open}
        width={800}
        onClose={() => {
          setOpen(false);
          if (onClose) {
            onClose();
          }
        }}
      >
        <FlexBox direction="column">
          { char instanceof Character && 
            <FlexBox>
              <CenteredMenu options={["Customizations","Character Settings"]} selected={view} onSelect={setView} />
            </FlexBox>
          }
          { renderView() }
        </FlexBox>
      </ModalOrDrawer>
    );
  }

  return (
    <>
      <Tooltip title={`Character Settings`}>
        <Settings 
          className="clickable"
          onClick={() => {
            setOpen(true);
            if (onOpen) {
              onOpen();
            }
          }}
        />
      </Tooltip>
      { renderExtras() }
    </>
  ); 
}

